import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "#app/utils/misc"

const buttonVariants = cva(
	"inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors outline-none focus-visible:ring-4 focus-within:ring-4 ring-ring ring-offset-2 disabled:pointer-events-none disabled:opacity-50 select-none",
	{
		variants: {
			variant: {
				default:
					"bg-secondary text-secondary-foreground hover:bg-link/80 hover:bg-secondary/80",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/80",
				outline:
					"border border-input bg-background hover:bg-accent hover:text-accent-foreground",
				secondary:
					"bg-accent rounded-lg text-accent-foreground hover:bg-accent/80",
				ghost:
					"hover:bg-accent hover:text-accent-foreground border border-accent border-opacity-20 hover:border-opacity-10",
				link: "underline-offset-4 underline hover:no-underline",
			},
			size: {
				default: "h-10 px-4 py-2 rounded-md",
				wide: "px-24 py-5",
				sm: "h-10 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				pill: "px-12 py-3 leading-3",
				icon: "h-10 w-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button"
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		)
	},
)
Button.displayName = "Button"

export { Button, buttonVariants }
